import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import Footer from "@/components/Footer";
import Blog from "@/components/Blog";
import SuccessStories from "@/components/SuccessStories";
import AboutCalculator from "@/components/AboutCalculator";
import GuidesSection from "@/components/GuidesSection";

/* Lightweight wrapper used by `/stories`, `/about-calculator`, `/blog`, and `/guides`
   routes. Renders just a thin header + the requested section component + the shared
   footer so these pages feel like proper subpages (reachable from the footer links)
   instead of inline sections on the home page. */
const SECTION_META = {
  stories: {
    ar: "قصص نجاح حقيقية",
    en: "Real success stories",
    Component: SuccessStories,
  },
  about: {
    ar: "ما هي حاسبة برولانسر؟",
    en: "What is the Prolancer Calculator?",
    Component: AboutCalculator,
  },
  blog: {
    ar: "مدوّنة برولانسر",
    en: "Prolancer blog",
    Component: Blog,
  },
  guides: {
    ar: "أدلّة أسعار الخدمات",
    en: "Service pricing guides",
    Component: GuidesSection,
  },
};

export default function SectionPage({ section, lang = "ar", theme = "dark" }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const meta = SECTION_META[section];
  const Section = meta?.Component;

  // Always scroll to the top when a section page mounts — otherwise clicking a
  // footer link (which sits at the bottom of the previous page) keeps the user
  // scrolled to the bottom and makes it look like "nothing happened".
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [section]);

  return (
    <div
      data-testid={`section-page-${section}`}
      dir={ar ? "rtl" : "ltr"}
      className={`min-h-screen ${dark ? "bg-[#182138] text-white" : "bg-white text-[#182138]"}`}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Thin sticky header with brand + back */}
      <header
        className={`sticky top-0 z-30 border-b backdrop-blur ${
          dark ? "bg-[#182138]/85 border-[#2e3a5c]" : "bg-white/85 border-[#e5e5ea]"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid={`${section}-back-home`}
            className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 border transition ${
              dark
                ? "border-[#2e3a5c] text-white hover:border-[#18c98f] hover:text-[#18c98f]"
                : "border-[#e5e5ea] text-[#182138] hover:border-[#18c98f] hover:text-[#12a777]"
            } ${ar ? "font-arabic" : "font-outfit"}`}
          >
            <ArrowLeft className={`h-3.5 w-3.5 ${ar ? "" : "rotate-180"}`} />
            {ar ? "العودة للحاسبة" : "Back to calculator"}
          </Link>
          <LogoLockup lang={lang} theme={theme} size="sm" />
        </div>
      </header>

      {/* Section hero chip */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div
          className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] font-extrabold ${
            dark ? "text-[#18c98f]" : "text-[#12a777]"
          }`}
        >
          <Sparkles className="h-3.5 w-3.5" />
          {ar ? meta.ar : meta.en}
        </div>
      </div>

      {/* The actual section */}
      {Section && <Section lang={lang} theme={theme} />}

      {/* Prominent back-to-calculator CTA before the footer */}
      <div className={`${dark ? "bg-[#182138]" : "bg-white"}`} dir={ar ? "rtl" : "ltr"}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <Link
            to="/"
            data-testid={`${section}-back-cta`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#18c98f] text-white text-sm font-extrabold shadow-[0_12px_28px_-10px_rgba(24,201,143,0.65)] hover:bg-[#18c98f] hover:scale-[1.03] active:scale-[0.98] transition-all font-arabic"
          >
            <ArrowLeft className={`h-4 w-4 ${ar ? "" : "rotate-180"}`} />
            {ar ? "الرجوع إلى الحاسبة" : "Back to the calculator"}
          </Link>
          <div className={`mt-3 text-xs ${dark ? "text-white/50" : "text-[#2d2c35]/55"} font-arabic`}>
            {ar ? "جرّب حاسبة التسعير وحاسبة الدخل مجانًا الآن" : "Try the pricing and earnings calculators for free"}
          </div>
        </div>
      </div>

      <Footer lang={lang} theme={theme} />
    </div>
  );
}
